import { gpx, kml } from "@mapbox/togeojson";

export interface GeoJSONFeatureCollection {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    properties: Record<string, unknown>;
    geometry: {
      type: string;
      coordinates: (number | number[] | number[][])[];
    };
  }>;
}

/**
 * Supported file types for geo data upload
 */
export const SUPPORTED_GEO_FILE_TYPES = [
  ".gpx",
  ".geojson",
  ".json",
  ".kml",
] as const;
export type SupportedGeoFileType = (typeof SUPPORTED_GEO_FILE_TYPES)[number];

/**
 * Validates if a file type is supported for geo data
 */
export function isSupportedGeoFileType(filename: string): boolean {
  const extension = filename.toLowerCase().split(".").pop();
  return SUPPORTED_GEO_FILE_TYPES.includes(
    `.${extension}` as SupportedGeoFileType
  );
}

/**
 * Converts a GPX file to GeoJSON
 */
export function gpxToGeoJSON(gpxContent: string): GeoJSONFeatureCollection {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(gpxContent, "application/xml");

    // Check for parsing errors
    const parserError = xmlDoc.querySelector("parsererror");
    if (parserError) {
      throw new Error("Invalid XML format in GPX file");
    }

    const geoJson = gpx(xmlDoc);

    // Ensure we return a FeatureCollection
    if (geoJson.type !== "FeatureCollection") {
      throw new Error("GPX conversion did not produce a FeatureCollection");
    }

    return geoJson as GeoJSONFeatureCollection;
  } catch (error) {
    throw new Error(
      `Failed to convert GPX to GeoJSON: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Converts a KML file to GeoJSON
 */
export function kmlToGeoJSON(kmlContent: string): GeoJSONFeatureCollection {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(kmlContent, "application/xml");

    // Check for parsing errors
    const parserError = xmlDoc.querySelector("parsererror");
    if (parserError) {
      throw new Error("Invalid XML format in KML file");
    }

    const geoJson = kml(xmlDoc);

    // Ensure we return a FeatureCollection
    if (geoJson.type !== "FeatureCollection") {
      throw new Error("KML conversion did not produce a FeatureCollection");
    }

    return geoJson as GeoJSONFeatureCollection;
  } catch (error) {
    throw new Error(
      `Failed to convert KML to GeoJSON: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Validates and parses a GeoJSON file
 */
export function parseGeoJSON(geoJsonContent: string): GeoJSONFeatureCollection {
  try {
    const parsed = JSON.parse(geoJsonContent);

    if (parsed.type !== "FeatureCollection") {
      throw new Error("GeoJSON must be a FeatureCollection");
    }

    if (!Array.isArray(parsed.features)) {
      throw new Error("GeoJSON FeatureCollection must have a features array");
    }

    // Basic validation of features
    for (const feature of parsed.features) {
      if (feature.type !== "Feature") {
        throw new Error("All items in features array must be Feature objects");
      }
      if (!feature.geometry || !feature.geometry.type) {
        throw new Error("Each feature must have a valid geometry");
      }
    }

    return parsed as GeoJSONFeatureCollection;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error("Invalid JSON format in GeoJSON file");
    }
    throw new Error(
      `Failed to parse GeoJSON: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Main function to process a geo file and convert it to GeoJSON
 */
export async function processGeoFile(
  file: File
): Promise<GeoJSONFeatureCollection> {
  if (!isSupportedGeoFileType(file.name)) {
    throw new Error(
      `Unsupported file type. Supported types: ${SUPPORTED_GEO_FILE_TYPES.join(", ")}`
    );
  }

  const content = await file.text();
  const extension = file.name.toLowerCase().split(".").pop();

  switch (extension) {
    case "gpx":
      return gpxToGeoJSON(content);
    case "kml":
      return kmlToGeoJSON(content);
    case "geojson":
    case "json":
      return parseGeoJSON(content);
    default:
      throw new Error(`Unsupported file extension: ${extension}`);
  }
}

/**
 * Validates that the GeoJSON contains useful data for an area
 */
export function validateareaGeoData(geoJson: GeoJSONFeatureCollection): {
  isValid: boolean;
  warnings: string[];
  summary: {
    totalFeatures: number;
    points: number;
    lines: number;
    polygons: number;
    tracks: number;
    waypoints: number;
  };
} {
  const warnings: string[] = [];
  let points = 0;
  let lines = 0;
  let polygons = 0;
  let tracks = 0;
  let waypoints = 0;

  for (const feature of geoJson.features) {
    const geomType = feature.geometry.type;
    const props = feature.properties || {};

    switch (geomType) {
      case "Point":
        points++;
        // Check if it's a waypoint
        if (props.name || props.wpt_name || props.sym) {
          waypoints++;
        }
        break;
      case "LineString":
      case "MultiLineString":
        lines++;
        // Check if it's a track
        if (props.name || props.trk_name || geomType.includes("LineString")) {
          tracks++;
        }
        break;
      case "Polygon":
      case "MultiPolygon":
        polygons++;
        break;
    }
  }

  // Validation rules
  let isValid = true;

  if (geoJson.features.length === 0) {
    warnings.push("No features found in the file");
    isValid = false;
  }

  if (points === 0 && lines === 0) {
    warnings.push(
      "No points or tracks found - this may not be useful area data"
    );
  }

  if (geoJson.features.length > 1000) {
    warnings.push(
      "Large number of features detected - this may impact performance"
    );
  }

  return {
    isValid,
    warnings,
    summary: {
      totalFeatures: geoJson.features.length,
      points,
      lines,
      polygons,
      tracks,
      waypoints,
    },
  };
}
